<template>
  <div>
    <h2
      class="font-[Poppins] font-black text-[clamp(24px,3.5vw,34px)] text-[#191c1e] mb-1.5 tracking-[-0.5px]"
    >
      Logotyp
    </h2>
    <p
      class="font-[Plus_Jakarta_Sans] text-[15px] text-[#6c7a77] leading-[1.65] mb-7 max-w-[580px]"
    >
      Emmalagat har två logotypvarianter: det fullständiga ordmärket och
      E-monogrammet. Använd alltid originalfilerna – ändra aldrig form, färg
      eller proportioner.
    </p>

    <!-- Full wordmark -->
    <PreviewBox label="Ordmärke — EmmalagatLogo.vue">
      <div class="flex flex-col gap-6">
        <!-- On light -->
        <div class="flex items-center gap-8">
          <div
            class="bg-white rounded-xl px-8 py-5 border border-[#eceef0] flex items-center justify-center"
          >
            <EmmalagatLogo width="163" />
          </div>
          <div class="flex flex-col gap-1">
            <p
              class="font-[Poppins] font-bold text-[11px] text-[#6c7a77] tracking-[1px] uppercase m-0"
            >
              På ljus bakgrund
            </p>
            <p class="font-[Plus_Jakarta_Sans] text-[13px] text-[#3c4947] m-0">
              Standardanvändning
            </p>
          </div>
        </div>
        <!-- On dark -->
        <div class="flex items-center gap-8">
          <div
            class="bg-[#191c1e] rounded-xl px-8 py-5 flex items-center justify-center"
          >
            <EmmalagatLogo width="163" color="white" />
          </div>
          <div class="flex flex-col gap-1">
            <p
              class="font-[Poppins] font-bold text-[11px] text-[#6c7a77] tracking-[1px] uppercase m-0"
            >
              På mörk bakgrund
            </p>
            <p class="font-[Plus_Jakarta_Sans] text-[13px] text-[#3c4947] m-0">
              Sidfot, mörkt tema
            </p>
          </div>
        </div>
        <!-- On teal -->
        <div class="flex items-center gap-8">
          <div
            class="bg-teal-500 rounded-xl px-8 py-5 flex items-center justify-center"
          >
            <EmmalagatLogo width="163" color="white" />
          </div>
          <div class="flex flex-col gap-1">
            <p
              class="font-[Poppins] font-bold text-[11px] text-[#6c7a77] tracking-[1px] uppercase m-0"
            >
              På teal bakgrund
            </p>
            <p class="font-[Plus_Jakarta_Sans] text-[13px] text-[#3c4947] m-0">
              CTA-sektioner
            </p>
          </div>
        </div>
      </div>
    </PreviewBox>

    <!-- Sizes -->
    <PreviewBox label="Storlekar — ordmärket">
      <div class="flex flex-col gap-4">
        <div class="flex items-center gap-4">
          <EmmalagatLogo width="48" />
          <span class="font-[Plus_Jakarta_Sans] text-[13px] text-[#6c7a77]"
            >width=48 — navbar (mobil)</span
          >
        </div>
        <div class="flex items-center gap-4">
          <EmmalagatLogo :size="48" />
          <span class="font-[Plus_Jakarta_Sans] text-[13px] text-[#6c7a77]"
            >size=48, width=auto — navbar (desktop)</span
          >
        </div>
        <div class="flex items-center gap-4">
          <EmmalagatLogo width="163" />
          <span class="font-[Plus_Jakarta_Sans] text-[13px] text-[#6c7a77]"
            >width=163 — sidfot</span
          >
        </div>
      </div>
    </PreviewBox>

    <!-- E monogram -->
    <PreviewBox label="E-monogram — /icon.svg (favicon)">
      <div class="flex items-end gap-8">
        <div class="flex flex-col items-center gap-2">
          <EmmalagatIcon :size="64" />
          <span class="font-[Plus_Jakarta_Sans] text-[11px] text-[#6c7a77]"
            >64 px</span
          >
        </div>
        <div class="flex flex-col items-center gap-2">
          <EmmalagatIcon :size="48" />
          <span class="font-[Plus_Jakarta_Sans] text-[11px] text-[#6c7a77]"
            >48 px</span
          >
        </div>
        <div class="flex flex-col items-center gap-2">
          <EmmalagatIcon :size="32" />
          <span class="font-[Plus_Jakarta_Sans] text-[11px] text-[#6c7a77]"
            >32 px</span
          >
        </div>
        <div class="flex flex-col items-center gap-2">
          <EmmalagatIcon :size="16" />
          <span class="font-[Plus_Jakarta_Sans] text-[11px] text-[#6c7a77]"
            >16 px</span
          >
        </div>
      </div>
    </PreviewBox>

    <!-- Don'ts -->
    <PreviewBox label="Undvik">
      <div class="flex flex-col gap-3 max-w-[420px]">
        <div
          class="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-100"
        >
          <span class="text-red-400 text-lg shrink-0">✕</span>
          <span class="font-[Plus_Jakarta_Sans] text-[13px] text-[#3c4947]"
            >Ändra inte logotypens färg (teal #13B8A7 är fast)</span
          >
        </div>
        <div
          class="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-100"
        >
          <span class="text-red-400 text-lg shrink-0">✕</span>
          <span class="font-[Plus_Jakarta_Sans] text-[13px] text-[#3c4947]"
            >Använd inte logotypen under 40&nbsp;px bredd</span
          >
        </div>
        <div
          class="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-100"
        >
          <span class="text-red-400 text-lg shrink-0">✕</span>
          <span class="font-[Plus_Jakarta_Sans] text-[13px] text-[#3c4947]"
            >Sträck eller snedvrid aldrig proportionerna</span
          >
        </div>
      </div>
    </PreviewBox>

    <CodeBlock :tabs="snippetTabs" />
  </div>
</template>

<script setup>
import EmmalagatLogo from "@/components/icons/EmmalagatLogo.vue";
import EmmalagatIcon from "@/components/icons/EmmalagatIcon.vue";
import PreviewBox from "../components/PreviewBox.vue";
import CodeBlock from "../components/CodeBlock.vue";

const htmlCode = `<!-- Ordmärke (SVG-komponent) -->
<!-- Använd antingen size (height, beräknar width auto)
     eller width (sätter bredd direkt) -->
<EmmalagatLogo :size="48" />     <!-- navbar desktop -->
<EmmalagatLogo width="163" />    <!-- sidfot -->

<!-- E-monogram (Vue-komponent, inline SVG) -->
<EmmalagatIcon :size="48" />`;

const cssCode = `/* Ordmärke – EmmalagatLogo.vue */
/* viewBox: 0 0 1060 308 */
/* Teal-färg: #13B8A7 */

/* Rekommenderade storlekar */
/* Navbar desktop: size=48 (height 48px, proportionell bredd) */
/* Navbar mobil:   size=48 */
/* Sidfot:         width=163 */
/* Minsta storlek: width=40px */

/* E-monogram – /icon.svg */
/* Cirkulär, 624×624 viewBox */
/* Teal bakgrund: #13B8A7, vit bokstav */
/* Används som favicon (icon.png, apple-touch-icon.png) */`;

const snippetTabs = [
  { label: "HTML", code: htmlCode },
  { label: "CSS", code: cssCode, lang: "css" },
];
</script>
